import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAnalyticsDashboard } from '../services/api';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { ArrowLeft, BarChart3, TrendingUp, Mail, MessageSquare, MousePointerClick, AlertTriangle, Users, Eye, RefreshCw } from '../icons';

const COLORS = ['#DC143C', '#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export default function AdminAnalytics() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAnalyticsDashboard(days);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load analytics', err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    loadData();
  }, [user, authLoading, navigate, loadData]);

  if (authLoading || loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
        <p>Failed to load analytics data.</p>
        <button onClick={loadData} style={{ marginTop: 12, padding: '8px 16px', background: '#DC143C', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }

  const { overview, charts, tables } = data;

  // Format chart dates
  const fmtChart = (arr) => (arr || []).map(r => ({
    ...r,
    date: new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    count: parseInt(r.count)
  }));

  const pageViewsData = fmtChart(charts.pageViewsByDay);
  const emailsData = fmtChart(charts.emailsSentByDay);
  const inquiriesData = fmtChart(charts.inquiriesByDay);
  const vendorClicksData = fmtChart(charts.vendorClicksByDay);

  const userRoleData = (tables.usersByRole || []).map(r => ({ name: r.role, value: parseInt(r.count) }));
  const emailStatusData = (tables.emailStats || []).map(r => ({ name: r.status, value: parseInt(r.count) }));
  const clickTypeData = (tables.clicksByType || []).map(r => ({ name: r.click_type, value: parseInt(r.count) }));

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 20px', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/admin')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 14, display: 'flex', alignItems: 'center', gap: 4 }}>
          <ArrowLeft size={16} /> Admin
        </button>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0, color: '#1f2937', display: 'flex', alignItems: 'center', gap: 8 }}>
          <BarChart3 size={24} /> Analytics Dashboard
        </h1>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {[7, 14, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              style={{ padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: days === d ? '2px solid #DC143C' : '2px solid #e5e7eb',
                background: days === d ? '#fef2f2' : '#fff',
                color: days === d ? '#DC143C' : '#374151' }}>
              {d}d
            </button>
          ))}
          <button onClick={loadData} style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard icon={<Eye size={20} />} label="Page Views" value={overview.totalPageViews} color="#3b82f6" />
        <StatCard icon={<Users size={20} />} label="Unique Visitors" value={overview.uniqueVisitors} color="#8b5cf6" />
        <StatCard icon={<Mail size={20} />} label="Emails Sent" value={overview.totalEmailsSent} color="#22c55e" />
        <StatCard icon={<MessageSquare size={20} />} label="Inquiries" value={overview.totalInquiries} color="#f59e0b" />
        <StatCard icon={<AlertTriangle size={20} />} label="API Errors" value={overview.totalApiErrors} color={overview.totalApiErrors > 0 ? '#ef4444' : '#9ca3af'} />
      </div>

      {/* Charts Row 1: Page Views + Emails */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
        <ChartCard title="Page Views" subtitle={`${overview.totalPageViews} views in ${days} days`}>
          {pageViewsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={pageViewsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="#dbeafe" strokeWidth={2} name="Views" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>

        <ChartCard title="Emails Sent" subtitle={`${overview.totalEmailsSent} sent in ${days} days`}>
          {emailsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={emailsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#22c55e" radius={[4, 4, 0, 0]} name="Emails" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>
      </div>

      {/* Charts Row 2: Inquiries + Vendor Clicks */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
        <ChartCard title="Inquiries" subtitle={`${overview.totalInquiries} inquiries in ${days} days`}>
          {inquiriesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={inquiriesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} name="Inquiries" />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>

        <ChartCard title="Vendor Clicks" subtitle="Profile views over time">
          {vendorClicksData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={vendorClicksData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#DC143C" fill="#fecdd3" strokeWidth={2} name="Clicks" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>
      </div>

      {/* Pie Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24, marginBottom: 24 }}>
        <ChartCard title="Users by Role">
          {userRoleData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={userRoleData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                  {userRoleData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>

        <ChartCard title="Email Status">
          {emailStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={emailStatusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                  {emailStatusData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>

        <ChartCard title="Click Types">
          {clickTypeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={clickTypeData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                  {clickTypeData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>
      </div>

      {/* Tables Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
        {/* Top Pages */}
        <ChartCard title="Top Pages" subtitle="Most visited pages">
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 600 }}>Page</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: 600 }}>Views</th>
                </tr>
              </thead>
              <tbody>
                {(tables.topPages || []).map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px 12px', color: '#374151' }}>{p.path}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1f2937' }}>{parseInt(p.count).toLocaleString()}</td>
                  </tr>
                ))}
                {(!tables.topPages || tables.topPages.length === 0) && (
                  <tr><td colSpan="2" style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>No data yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </ChartCard>

        {/* Top Vendors */}
        <ChartCard title="Top Vendors" subtitle="Most clicked vendor profiles">
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 600 }}>Vendor</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: 600 }}>Clicks</th>
                </tr>
              </thead>
              <tbody>
                {(tables.topVendors || []).map((v, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px 12px', color: '#374151' }}>{v.business_name}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1f2937' }}>{parseInt(v.clicks).toLocaleString()}</td>
                  </tr>
                ))}
                {(!tables.topVendors || tables.topVendors.length === 0) && (
                  <tr><td colSpan="2" style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>No data yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </ChartCard>
      </div>

      {/* Error Log */}
      {(tables.recentErrors || []).length > 0 && (
        <ChartCard title="Recent API Errors" subtitle={`${overview.totalApiErrors} errors in ${days} days`}>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#6b7280' }}>Endpoint</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#6b7280' }}>Method</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#6b7280' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#6b7280' }}>Error</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#6b7280' }}>Time</th>
                </tr>
              </thead>
              <tbody>
                {tables.recentErrors.map((e, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px', color: '#374151', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.endpoint}</td>
                    <td style={{ padding: '8px' }}><span style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{e.method}</span></td>
                    <td style={{ padding: '8px' }}><span style={{ background: '#fee2e2', color: '#b91c1c', padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{e.status_code}</span></td>
                    <td style={{ padding: '8px', color: '#6b7280', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.error_message}</td>
                    <td style={{ padding: '8px', color: '#9ca3af', whiteSpace: 'nowrap' }}>{new Date(e.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#1f2937' }}>{(value || 0).toLocaleString()}</div>
        <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 24px' }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function EmptyChart() {
  return (
    <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>
      <div style={{ textAlign: 'center' }}>
        <BarChart3 size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
        <p>No data for this period</p>
      </div>
    </div>
  );
}
